/**
 * Connector geometry (F-38) — PURE logic: no React, no DOM, no Yjs. Everything
 * here works in BOARD units, like the rest of the camera-layer geometry
 * (docs/04 §5); the connectors layer renders inside the same transform, so it
 * never converts to screen space at all.
 *
 * The model, and why it needs no stored coordinates: a connector endpoint is a
 * PORT — an element id plus one of its four edges (`@kola/shared`
 * `ConnectorEndpoint`). Its board point is derived from the element's RESOLVED
 * rect on every render pass, exactly like a comment pin's anchor (docs/04 §3).
 * That is what makes "the line follows the item" true by construction rather
 * than by event wiring: a move, a resize, a column resize, a row sort and a
 * peer's awareness-streamed drag preview all change the rect, and the curve is
 * re-derived from it — there is nothing to keep in sync and nothing to drift.
 *
 * The curve leaves each port along that side's OUTWARD normal, so a line into a
 * left port always arrives from the left however the two elements are placed.
 * That is the whole reason connectors bow instead of running straight: a
 * straight segment between two ports reads as ambiguous (which side did it
 * attach to?) the moment the elements are not neatly opposed.
 */
import { CONNECTOR_SIDES, type ConnectorSide, type ElementType } from "@kola/shared";

import type { BoardRect } from "../../registries/toolbar-actions/index.js";

export interface Point {
  x: number;
  y: number;
}

/**
 * Everything but a pen stroke takes connectors (F-38). A drawing is ink: its
 * bounding box is mostly empty, it is deliberately pointer-inert except along
 * the stroke, and an edge midpoint of that box is nowhere the user actually
 * drew — a line landing there would attach to nothing visible.
 */
export function canConnectTo(type: ElementType): boolean {
  return type !== "drawing";
}

/** Outward unit normal of a side — the direction the curve leaves the port. */
export function sideNormal(side: ConnectorSide): Point {
  switch (side) {
    case "top":
      return { x: 0, y: -1 };
    case "bottom":
      return { x: 0, y: 1 };
    case "left":
      return { x: -1, y: 0 };
    case "right":
      return { x: 1, y: 0 };
  }
}

/** Board point of one port: the midpoint of that edge. */
export function portPoint(rect: BoardRect, side: ConnectorSide): Point {
  const cx = rect.x + rect.w / 2;
  const cy = rect.y + rect.h / 2;
  switch (side) {
    case "top":
      return { x: cx, y: rect.y };
    case "bottom":
      return { x: cx, y: rect.y + rect.h };
    case "left":
      return { x: rect.x, y: cy };
    case "right":
      return { x: rect.x + rect.w, y: cy };
  }
}

/** All four ports of a rect, clockwise from the top. */
export function portPoints(rect: BoardRect): Array<{ side: ConnectorSide; point: Point }> {
  return CONNECTOR_SIDES.map((side) => ({ side, point: portPoint(rect, side) }));
}

/* ------------------------------------------------------------------ */
/* The curve                                                           */
/* ------------------------------------------------------------------ */

/** Shortest handle length (board units) — keeps a bow on very short lines. */
export const MIN_CURVE_HANDLE = 24;
/** Longest handle length — past this the bow stops growing with distance. */
export const MAX_CURVE_HANDLE = 160;

/**
 * Cubic-bezier handle length for a span: proportional to the distance so short
 * links stay tight and long ones sweep, clamped at both ends so neither a
 * zero-length span (two stacked elements) nor a cross-board one degenerates.
 */
export function curveHandle(from: Point, to: Point): number {
  const distance = Math.hypot(to.x - from.x, to.y - from.y);
  return Math.min(MAX_CURVE_HANDLE, Math.max(MIN_CURVE_HANDLE, distance * 0.4));
}

/** The two control points of the connector's cubic bezier. */
export function connectorControlPoints(
  from: Point,
  fromSide: ConnectorSide,
  to: Point,
  toSide: ConnectorSide,
): [Point, Point] {
  const handle = curveHandle(from, to);
  const a = sideNormal(fromSide);
  const b = sideNormal(toSide);
  return [
    { x: from.x + a.x * handle, y: from.y + a.y * handle },
    { x: to.x + b.x * handle, y: to.y + b.y * handle },
  ];
}

/** SVG `d` for the connector — one cubic bezier, in board units. */
export function connectorPath(
  from: Point,
  fromSide: ConnectorSide,
  to: Point,
  toSide: ConnectorSide,
): string {
  const [c1, c2] = connectorControlPoints(from, fromSide, to, toSide);
  return `M ${round(from.x)} ${round(from.y)} C ${round(c1.x)} ${round(c1.y)}, ${round(
    c2.x,
  )} ${round(c2.y)}, ${round(to.x)} ${round(to.y)}`;
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}

/* ------------------------------------------------------------------ */
/* Proximity: which port a pointer means                               */
/* ------------------------------------------------------------------ */

/** Distance from `p` to the segment `a`–`b` (a degenerate segment = a point). */
export function distanceToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/**
 * Which edge of `rect` a board point is "around" — the nearest side, or null
 * when the point is further than `threshold` from every edge. Measured against
 * the EDGES, not the ports, so running the cursor along an element's border
 * lights up that whole side (F-38: "when the cursor is around the edge of each
 * side, the starting point shows") instead of only its exact midpoint. A point
 * deep inside a large element is near no edge and returns null — the element
 * body stays a plain drag surface.
 */
export function nearestSide(
  rect: BoardRect,
  point: Point,
  threshold: number,
): ConnectorSide | null {
  const right = rect.x + rect.w;
  const bottom = rect.y + rect.h;
  const edges: Array<{ side: ConnectorSide; a: Point; b: Point }> = [
    { side: "top", a: { x: rect.x, y: rect.y }, b: { x: right, y: rect.y } },
    { side: "right", a: { x: right, y: rect.y }, b: { x: right, y: bottom } },
    { side: "bottom", a: { x: rect.x, y: bottom }, b: { x: right, y: bottom } },
    { side: "left", a: { x: rect.x, y: rect.y }, b: { x: rect.x, y: bottom } },
  ];
  let best: ConnectorSide | null = null;
  let bestDistance = threshold;
  for (const edge of edges) {
    const d = distanceToSegment(point, edge.a, edge.b);
    // `<` not `<=`: ties keep the earlier side in CONNECTOR_SIDES order, so a
    // corner resolves deterministically (top before right before bottom…).
    if (d < bestDistance) {
      bestDistance = d;
      best = edge.side;
    }
  }
  return best;
}

/** One connectable port on the board, with its resolved board point. */
export interface PortCandidate {
  elementId: string;
  side: ConnectorSide;
  point: Point;
}

/** Every port of every candidate element — the drop targets during a drag. */
export function portCandidates(
  rects: Iterable<{ id: string; rect: BoardRect }>,
): PortCandidate[] {
  const out: PortCandidate[] = [];
  for (const { id, rect } of rects) {
    for (const { side, point } of portPoints(rect)) {
      out.push({ elementId: id, side, point });
    }
  }
  return out;
}

/**
 * The port a dragged connector end SNAPS to: the closest candidate within
 * `maxDistance` of the pointer, or null (the drag then ends on empty canvas and
 * creates nothing). Ties resolve to the first candidate in input order, which
 * callers keep in the elements' paint order — so a port on a frontmost element
 * wins over one exactly as close on an element behind it.
 */
export function nearestPort(
  candidates: readonly PortCandidate[],
  point: Point,
  maxDistance: number,
): PortCandidate | null {
  let best: PortCandidate | null = null;
  let bestDistance = maxDistance;
  for (const candidate of candidates) {
    const d = Math.hypot(candidate.point.x - point.x, candidate.point.y - point.y);
    if (d < bestDistance) {
      bestDistance = d;
      best = candidate;
    }
  }
  return best;
}

/** Axis-aligned bounding box of a connector's curve, for viewport culling. */
export function connectorBounds(
  from: Point,
  fromSide: ConnectorSide,
  to: Point,
  toSide: ConnectorSide,
): BoardRect {
  const [c1, c2] = connectorControlPoints(from, fromSide, to, toSide);
  // The control polygon's hull contains the curve — cheap and never too small.
  const xs = [from.x, c1.x, c2.x, to.x];
  const ys = [from.y, c1.y, c2.y, to.y];
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return { x: minX, y: minY, w: Math.max(...xs) - minX, h: Math.max(...ys) - minY };
}
